import React, { useState, useEffect, useContext, useRef, useLayoutEffect } from "react";
import { Typography } from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AuthContext } from "../../context/Auth/AuthContext";
import { useWhiteLabel, getWLLogo } from "../../context/WhiteLabel/WhiteLabelContext";
import { useThemeContext } from "../../context/DarkMode";
import { useBannerActive } from "../../context/BannerActive/BannerActiveContext";
import api from "../../services/api";
import { getSocket } from "../../services/socket-io";

const ITEM_DURATION = 6000;
const INTERRUPT_DURATION = 8000;
const RETRY_EMPTY = 30000;
const READ_KEY = "waia_read_announcements";
const GREETING_KEY = "waia_greeting_shown";
const BANNER_MAX_SHOWS = 3;
const BANNER_SHOWN_PREFIX = "banner_shown_";

const getGreeting = (name) => {
  const h = new Date().getHours();
  const saludo = h < 12 ? "Buenos días" : h < 18 ? "Buenas tardes" : "Buenas noches";
  // Nombre completo (antes truncaba al primer token, ej. "Dr." para "Dr.
  // Roberto Gazzera") — classes.greeting ya maneja overflow/ellipsis.
  return `${saludo}, ${name} 👋`;
};

const getReadIds = () => {
  try { return new Set(JSON.parse(localStorage.getItem(READ_KEY) || "[]")); }
  catch { return new Set(); }
};

const persistReadId = (id) => {
  const ids = getReadIds();
  ids.add(String(id));
  localStorage.setItem(READ_KEY, JSON.stringify([...ids]));
};

// feat_145: cada BannerMessage se muestra máximo BANNER_MAX_SHOWS veces por sesión.
// El contador vive en localStorage por id de banner; se limpia en cada login nuevo.
const getBannerShowCount = (id) =>
  parseInt(localStorage.getItem(`${BANNER_SHOWN_PREFIX}${id}`) || "0", 10);

const incrementBannerShowCount = (id) => {
  const next = getBannerShowCount(id) + 1;
  localStorage.setItem(`${BANNER_SHOWN_PREFIX}${id}`, String(next));
};

const clearBannerShowCounts = () => {
  Object.keys(localStorage)
    .filter(k => k.startsWith(BANNER_SHOWN_PREFIX))
    .forEach(k => localStorage.removeItem(k));
};

const useStyles = makeStyles((theme) => ({
  bannerRoot: {
    display: "flex",
    alignItems: "center",
    width: "100%",
    minWidth: 0,
    overflow: "hidden",
  },
  companyBlock: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    flexShrink: 0,
    marginRight: "3em",
    [theme.breakpoints.down("lg")]: {
      marginRight: "1.5em",
    },
  },
  companyName: {
    color: "inherit",
    fontSize: 18,
    fontWeight: 500,
    whiteSpace: "nowrap",
    [theme.breakpoints.down("lg")]: {
      fontSize: 16,
    },
  },
  separator: {
    color: "inherit",
    opacity: 0.5,
    fontSize: 18,
    lineHeight: 1,
    [theme.breakpoints.down("lg")]: {
      fontSize: 16,
    },
  },
  // Viewport FLEXIBLE (feat_140): crece (flexGrow:1) para usar el espacio sobrante
  // dentro del .title (que ya es flexGrow:1) y se encoge (flexShrink:1) si falta.
  // minWidth 280 protege los íconos derechos sin pisarlos; SIN maxWidth → en 4K
  // crece todo lo que el contenido necesite (banner azul de 773px entra estático).
  // companyBlock + summaryStats + íconos derechos tienen flexShrink:0 → prioridad.
  rotatingZone: {
    flexGrow: 1,
    flexShrink: 1,
    minWidth: 280,
    display: "flex",
    alignItems: "center",
    overflow: "hidden",
    whiteSpace: "nowrap",
    [theme.breakpoints.down("md")]: {
      minWidth: 280,
      flexShrink: 1,
    },
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  // Track que se desliza de derecha a izquierda en loop infinito.
  // paddingLeft:100% arranca el contenido fuera del borde derecho;
  // translateX(-100%) lo saca por la izquierda. Duración inline = velocidad constante.
  marqueeTrack: {
    display: "inline-block",
    whiteSpace: "nowrap",
    paddingLeft: "100%",
    willChange: "transform",
    animationName: "$marquee",
    animationTimingFunction: "linear",
    animationIterationCount: "infinite",
  },
  // Span fantasma (feat_139): mide el ancho NATURAL del texto sin constraints
  // (off-screen, no afecta layout). scrollWidth del ghost vs containerRef.clientWidth
  // decide estático vs marquee. Padding del badge (el variant más ancho).
  measureGhost: {
    position: "absolute",
    visibility: "hidden",
    whiteSpace: "nowrap",
    pointerEvents: "none",
    top: -9999,
    left: -9999,
    fontSize: 14,
    fontWeight: 500,
    padding: "2px 10px",
  },
  // Track estático (feat_138): el texto entra completo → sin animación, ellipsis si justo.
  staticTrack: {
    display: "inline-block",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
    maxWidth: "100%",
    animation: "$fadeIn 0.4s ease",
  },
  badge: {
    backgroundColor: theme.palette.primary.main,
    color: "#fff",
    padding: "2px 10px",
    borderRadius: 4,
    fontWeight: "bold",
    fontSize: 13,
    whiteSpace: "nowrap",
    display: "inline-block",
  },
  // Saludos / métricas: textos cortos, estáticos. nowrap + ellipsis → nunca rompen vertical.
  greeting: {
    color: theme.palette.text.primary,
    fontSize: 14,
    fontWeight: 500,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "100%",
    display: "inline-block",
    animation: "$fadeIn 0.4s ease",
  },
  metric: {
    color: theme.palette.text.primary,
    fontSize: 13,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
    overflow: "hidden",
    maxWidth: "100%",
    display: "inline-block",
    animation: "$fadeIn 0.4s ease",
  },
  "@keyframes marquee": {
    "0%": { transform: "translateX(0)" },
    "100%": { transform: "translateX(-100%)" },
  },
  "@keyframes fadeIn": {
    from: { opacity: 0, transform: "translateY(4px)" },
    to: { opacity: 1, transform: "translateY(0)" },
  },
}));

// Velocidad del marquee (feat_144):
// - BannerMessage con durationSec configurado → el texto cruza la pantalla en
//   EXACTAMENTE durationSec segundos (sin clamp). durationSec gobierna la velocidad.
// - Resto (Announcements, banner sin durationSec) → velocidad constante por longitud:
//   ~0.35s por caracter, clamp [12s, 60s]. Comportamiento anterior intacto.
const marqueeDuration = (item) => {
  if (item && item._isBanner && item.durationSec != null) {
    return `${item.durationSec}s`;
  }
  const len = (item?.text || "").length;
  return `${Math.min(60, Math.max(12, len * 0.35))}s`;
};

const AppBanner = ({ showLogo }) => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const { whiteLabel } = useWhiteLabel();
  const { darkMode } = useThemeContext();
  const { setBannerActive } = useBannerActive();
  const [displayItem, setDisplayItem] = useState(null);

  // Publica al layout si hay un item activo en el marquee (feat_137)
  useEffect(() => {
    setBannerActive(displayItem !== null);
  }, [displayItem, setBannerActive]);

  // feat_138/139: marquee dinámico. contentRef = span fantasma (measureGhost) sin
  // constraints → su scrollWidth es el ancho NATURAL del texto. Se compara vs el
  // viewport fijo (containerRef). Si entra → estático con ellipsis; si no → marquee.
  const containerRef = useRef(null);
  const contentRef = useRef(null);
  const [needsMarquee, setNeedsMarquee] = useState(false);

  const measureOverflow = () => {
    if (!displayItem || !contentRef.current || !containerRef.current) {
      setNeedsMarquee(false);
      return;
    }
    const overflow = contentRef.current.scrollWidth > containerRef.current.clientWidth;
    setNeedsMarquee(overflow);
  };

  useLayoutEffect(() => {
    // rAF defensivo: mide en el próximo frame para asegurar el ghost pintado (font load) (feat_139)
    const raf = requestAnimationFrame(() => measureOverflow());
    return () => cancelAnimationFrame(raf);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayItem]);

  useEffect(() => {
    const onResize = () => measureOverflow();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [displayItem]);

  // Data refs — always current, safe inside timer callbacks
  const announcementsRef = useRef([]);
  const bannerMessagesRef = useRef([]);
  const readIdsRef = useRef(getReadIds());
  const userNameRef = useRef(null);
  const bannerMsgIdxRef = useRef(0);

  const timerRef = useRef(null);
  const queueRef = useRef(null);
  const queueIdxRef = useRef(0);
  const greetingShownRef = useRef(!!sessionStorage.getItem(GREETING_KEY));
  const interruptRef = useRef(null);
  const advanceRef = useRef(null);

  // Track user id to detect login (null → id)
  const prevUserIdRef = useRef(null);

  useEffect(() => { userNameRef.current = user?.name || null; }, [user?.name]);

  // Reset greeting when a new login happens (user.id goes from null/different to current id)
  useEffect(() => {
    if (user?.id && prevUserIdRef.current !== user?.id) {
      if (prevUserIdRef.current === null) {
        // Fresh login — reset greeting so it shows again
        sessionStorage.removeItem(GREETING_KEY);
        greetingShownRef.current = false;
        // feat_145: nuevo login → resetear contadores de visualización de banners
        clearBannerShowCounts();
      }
      prevUserIdRef.current = user?.id;
    } else if (!user?.id) {
      prevUserIdRef.current = null;
    }
  }, [user?.id]);

  const buildQueue = (skipGreeting) => {
    const items = [];

    // 1. GREETING
    if (!skipGreeting && !greetingShownRef.current && userNameRef.current) {
      items.push({ type: "greeting", text: getGreeting(userNameRef.current) });
    }

    // 2. AVISOS — unread announcements
    announcementsRef.current
      .filter(a => !readIdsRef.current.has(String(a.id)))
      .forEach(a => items.push({
        type: "message",
        text: `📢 ${a.message}`,
        id: a.id,
        durationMs: a.durationSec != null ? a.durationSec * 1000 : ITEM_DURATION
      }));

    // 3. BANNER MESSAGE — rotate index, máx BANNER_MAX_SHOWS por sesión (feat_145)
    // Solo entran los banners que aún no llegaron al límite de visualizaciones.
    const eligibleBanners = bannerMessagesRef.current.filter(
      b => getBannerShowCount(b.id) < BANNER_MAX_SHOWS
    );
    if (eligibleBanners.length > 0) {
      const idx = bannerMsgIdxRef.current % eligibleBanners.length;
      const b = eligibleBanners[idx];
      items.push({
        type: "message",
        text: b.message,
        _isBanner: true,
        bannerId: b.id,
        durationSec: b.durationSec != null ? b.durationSec : null,
        durationMs: b.durationSec != null ? b.durationSec * 1000 : ITEM_DURATION
      });
    }

    return items;
  };

  advanceRef.current = () => {
    clearTimeout(timerRef.current);

    // Handle socket interrupt
    if (interruptRef.current) {
      const item = interruptRef.current;
      interruptRef.current = null;
      if (item.id) {
        persistReadId(item.id);
        readIdsRef.current = getReadIds();
      }
      setDisplayItem(item);
      timerRef.current = setTimeout(() => {
        greetingShownRef.current = true;
        sessionStorage.setItem(GREETING_KEY, "1");
        queueRef.current = buildQueue(true);
        queueIdxRef.current = 0;
        advanceRef.current();
      }, INTERRUPT_DURATION);
      return;
    }

    // Rebuild queue when exhausted
    if (!queueRef.current || queueIdxRef.current >= queueRef.current.length) {
      const skip = greetingShownRef.current;
      // Advance banner message index for next cycle
      if (bannerMessagesRef.current.length > 1) {
        bannerMsgIdxRef.current = (bannerMsgIdxRef.current + 1) % bannerMessagesRef.current.length;
      }
      queueRef.current = buildQueue(skip);
      queueIdxRef.current = 0;
    }

    if (queueRef.current.length === 0) {
      setDisplayItem(null);
      timerRef.current = setTimeout(() => advanceRef.current(), RETRY_EMPTY);
      return;
    }

    const item = queueRef.current[queueIdxRef.current++];

    if (item.type === "greeting") {
      greetingShownRef.current = true;
      sessionStorage.setItem(GREETING_KEY, "1");
    }
    if (item.id) {
      persistReadId(item.id);
      readIdsRef.current = getReadIds();
    }
    // feat_145: contar la visualización real del banner (no en buildQueue)
    if (item._isBanner && item.bannerId != null) {
      incrementBannerShowCount(item.bannerId);
    }

    setDisplayItem(item);

    const itemDuration = item.durationMs ?? ITEM_DURATION;
    if (itemDuration === 0) {
      // durationSec=0: banner estático, no avanzar automáticamente
      return;
    }
    timerRef.current = setTimeout(() => advanceRef.current(), itemDuration);
  };

  // Load data FIRST, then start cycle
  useEffect(() => {
    if (!user?.id || user.isSuperAdmin) return;

    // Reset cycle on (re)login
    clearTimeout(timerRef.current);
    queueRef.current = null;
    queueIdxRef.current = 0;
    setDisplayItem(null);

    Promise.all([
      api.get("/announcements").then(r => { announcementsRef.current = r.data || []; }).catch(() => {}),
      api.get("/banner-messages").then(r => { bannerMessagesRef.current = r.data || []; }).catch(() => {}),
    ]).finally(() => {
      advanceRef.current();
    });

    return () => clearTimeout(timerRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Socket: real-time announcements
  useEffect(() => {
    if (!user?.id || user.isSuperAdmin) return;

    const socket = getSocket();

    const handleAnnouncement = (data) => {
      if (data?.action === "create" && data.announcement) {
        const ann = data.announcement;
        announcementsRef.current = [ann, ...announcementsRef.current.filter(a => a.id !== ann.id)];
        interruptRef.current = { type: "message", text: `📢 ${ann.message}`, id: ann.id };
        advanceRef.current();
      }
    };
    socket.on("announcement", handleAnnouncement);

    // SINGLETON getSocket(): off() por referencia, NUNCA disconnect().
    return () => socket.off("announcement", handleAnnouncement);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id]);

  // Superadmin view
  if (!user?.id || user.isSuperAdmin) {
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        {showLogo && <img src={getWLLogo(whiteLabel, darkMode)} alt={whiteLabel?.name || "WAIA"} style={{ height: 30 }} />}
        <Typography variant="body2" noWrap style={{ color: "inherit", opacity: 0.7 }}>
          Superadmin
        </Typography>
      </div>
    );
  }

  const companyName = user.company?.name || "";

  const renderContent = () => {
    if (!displayItem) return null;

    // Nodo visible según tipo (estilos intactos: overflow/maxWidth/ellipsis).
    let innerNode;
    if (displayItem.type === "greeting") {
      innerNode = <span className={classes.greeting}>{displayItem.text}</span>;
    } else if (displayItem.type === "message") {
      innerNode = <span className={classes.badge}>{displayItem.text}</span>;
    } else {
      innerNode = <Typography component="span" className={classes.metric}>{displayItem.text}</Typography>;
    }

    // Si no entra → marquee animado; si entra → estático con ellipsis.
    return (
      <>
        {/* feat_139: ghost sin constraints → contentRef mide el ancho natural real */}
        <span ref={contentRef} className={classes.measureGhost} aria-hidden="true">
          {displayItem.text}
        </span>
        <div
          key={displayItem.text}
          className={needsMarquee ? classes.marqueeTrack : classes.staticTrack}
          style={needsMarquee ? { animationDuration: marqueeDuration(displayItem) } : undefined}
        >
          {innerNode}
        </div>
      </>
    );
  };

  return (
    <div className={classes.bannerRoot}>
      <div className={classes.companyBlock}>
        {showLogo && <img src={getWLLogo(whiteLabel, darkMode)} alt={whiteLabel?.name || "WAIA"} style={{ height: 30 }} />}
        {showLogo && companyName && <span className={classes.separator}>·</span>}
        {companyName && (
          <Typography component="span" className={classes.companyName}>
            {companyName}
          </Typography>
        )}
      </div>
      <div className={classes.rotatingZone} ref={containerRef}>
        {renderContent()}
      </div>
    </div>
  );
};

export default AppBanner;
